import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import {
    type AllowedPartners,
    TemplateStatus,
    useExpertUniversityConfiguration,
    useExpertUniversityStore,
} from "@expert/expert-university";
import { trackFullStoryEvent } from "@expert/monitoring";
import {
    Accordion,
    Anchor,
    Badge,
    Button,
    Container,
    Group,
    LoadingOverlay,
    Overlay,
    ScrollArea,
    SimpleGrid,
    Stack,
    Switch,
    Text,
} from "@mantine/core";
import { type FC, useEffect, useMemo, useState } from "react";

import { Warning } from "@/common-ui";
import { LoadingAnimation } from "@/common-ui/icons/sales";
import sharedStyles from "@/common-ui/sharedStyles.module.css";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { useFeatureFlag, userCache } from "@/shared-utils";

import type { TrainingSession, TrainingSessionEvaluationResults } from "../../types";

import { useEmployeeTrainingSessions } from "../../hooks/useEmployeeTrainingSessions";
import { useLiveKitCallStartInterstitial } from "../../hooks/useLiveKitCallStartInterstitial";
import { useTrainingEvaluationResults } from "../../hooks/useTrainingEvaluationResults";
import { useTrainingProfiles } from "../../hooks/useTrainingProfiles";
import { getProfileBlockLabel, groupProfilesByLearningBlock, isEvaluationTranscriptContent } from "../../utils";
import classes from "../ExpertUniversityHome.module.css";
import { LaunchCallSandboxButton } from "../LaunchCallSandboxButton";
import { Sidebar } from "../Sidebar";
import { TrainingProfileTile } from "../TrainingProfileTile";
import AscendEvaluationPage from "./AscendEvaluationPage";
import EvaluationResultsOverlay from "./EvaluationResultsOverlay";
import { EvaluationRetryOverlay } from "./EvaluationRetryOverlay";
import styles from "./ExpertUniversityLearningBlocks.module.css";
import TrainingEvaluationInterstitial from "./TrainingEvaluationInterstitial";

const PROFILE_TESTING_GROUP = "HQDOMAIN_APP-NON-VZW-APP-SOLUTO-TRAINING-ASSIST-";

export const ExpertUniversityLearningBlocks: FC = () => {
    const logger = useLogger({ module: "Expert University Learning Blocks" });

    const { trainingPartner: partner, setTaskInSandbox } = useExpertUniversityConfiguration();
    const { eventBus: expertUniversitySdkEventBus } = useExpertUniversityStore.getState();

    // Evaluation-results flow (ASCEND pilot only — this component is the pilot's entry point).
    const {
        evaluationResults,
        pendingReason,
        hasRetryableError,
        retryEvaluation,
        clearEvaluationResults,
        viewResultsBySessionId,
    } = useTrainingEvaluationResults();

    // Feature flags
    const { data: isExpertUniversityAdminUser } = useFeatureFlag<boolean>("EWP-isExpertUniversityAdminUser");

    const shouldShowInProgressProfiles =
        userCache.groups.includes(PROFILE_TESTING_GROUP) || isExpertUniversityAdminUser;
    // `undefined` means "not yet touched" so the derived value tracks the async admin flag until the
    // expert explicitly toggles the Switch; capturing the initial flag value would freeze in a wrong
    // default for admin-only users (the flag is undefined until it resolves).
    const [showInProgressOverride, setShowInProgressOverride] = useState<boolean | undefined>(undefined);
    const showInProgressProfiles = showInProgressOverride ?? shouldShowInProgressProfiles;
    const { callStartScreen, beginCallStart } = useLiveKitCallStartInterstitial();
    // `undefined` means "not yet touched" → default to all blocks open without an effect/flash.
    const [openBlocks, setOpenBlocks] = useState<string[] | undefined>(undefined);
    // "View results" opens a summary overlay for the selected profile's last session; from there the
    // expert can jump to the full transcript (AscendEvaluationPage) or go back to the accordion.
    const [resultsOverlay, setResultsOverlay] = useState<{ session: TrainingSession; title: string } | undefined>(
        undefined,
    );

    const correlationId = crypto.randomUUID();
    const partnerTrainingChallengeProfiles = useTrainingProfiles("asurion", partner as AllowedPartners, correlationId);

    // Flip the query key when evaluationResults appears/clears (mirrors ExpertUniversityHome) so the
    // training-sessions query refetches once a session's evaluation has persisted, picking up the
    // challenge card's updated status/"View results" state.
    const trainingSessionsViewFlag = useMemo(
        () => (evaluationResults === undefined ? `home-${partner}` : `eval-${partner}`),
        [evaluationResults, partner],
    );
    const { data: expertTrainingSessions } = useEmployeeTrainingSessions(
        trainingSessionsViewFlag,
        userCache.employeeId,
        partner as AllowedPartners,
    );

    // Clear any stale reconnect flag on mount so an earlier abnormal websocket close does not
    // cause a fresh pilot session to be misclassified as a reconnect attempt (Home does this too).
    useEffect(() => {
        useExpertUniversityStore.getState().setReconnect(false);
    }, []);

    const filtered = showInProgressProfiles
        ? partnerTrainingChallengeProfiles
        : partnerTrainingChallengeProfiles?.filter((profile) => profile.status === TemplateStatus.IN_PRODUCTION);

    const groups = useMemo(() => groupProfilesByLearningBlock(filtered), [filtered]);

    // The "Unassigned" block only appears while in-progress profiles are shown; otherwise it is hidden.
    const visibleGroups = useMemo(
        () => (showInProgressProfiles ? groups : groups.filter((group) => !group.isUnassigned)),
        [groups, showInProgressProfiles],
    );
    // "Expand all" opens every currently-visible block.
    const visibleKeys = useMemo(() => visibleGroups.map((group) => group.key), [visibleGroups]);
    // Default-open set: every visible block except "Unassigned", which starts collapsed.
    const defaultOpenKeys = useMemo(
        () => visibleGroups.filter((group) => !group.isUnassigned).map((group) => group.key),
        [visibleGroups],
    );

    // Default to the default-open set until the expert explicitly changes the selection.
    const value = openBlocks ?? defaultOpenKeys;

    // Branch order mirrors ExpertUniversityHome: loading -> results -> error -> normal view.
    // "evaluating" and "scoring" are the two phases of the one post-call flow. The plain overlay stays
    // for viewing a stored past result ("loading") and for session start-up, where the "call complete"
    // framing would be misleading.
    if (pendingReason === "evaluating" || pendingReason === "scoring") {
        // Strict boolean so only the hangup flag changes the copy. Read at render: the SDK sets it
        // before the task-completion remount, and this component does not subscribe to the store.
        const closedByServer = useExpertUniversityStore.getState().callClosedByServer === true;
        return (
            <>
                <TrainingEvaluationInterstitial
                    phase={pendingReason === "scoring" ? "scoring" : "submitting"}
                    hasError={hasRetryableError}
                    closedByServer={closedByServer}
                />
                {hasRetryableError && <EvaluationRetryOverlay onRetry={retryEvaluation} />}
            </>
        );
    }

    if (callStartScreen) {
        return callStartScreen;
    }

    if (pendingReason === "loading") {
        return (
            <LoadingOverlay
                visible
                loaderProps={{
                    children: (
                        <Container maw="50%">
                            <LoadingAnimation />
                            <Text>Loading results...it may take up to 20 seconds.</Text>
                        </Container>
                    ),
                }}
                overlayProps={{
                    backgroundOpacity: 1,
                    color: "var(--exwo-color-app-bg)",
                }}
            />
        );
    }

    if (evaluationResults && isEvaluationTranscriptContent(evaluationResults)) {
        return (
            <div className={classes.background}>
                <AscendEvaluationPage
                    evaluationResults={evaluationResults}
                    onCompleteEvaluation={() => {
                        clearEvaluationResults();
                        setResultsOverlay(undefined);
                    }}
                />
            </div>
        );
    }

    if (evaluationResults?.ErrorCode) {
        return (
            <Overlay data-testid="return-home-screen" color="var(--exwo-color-app-bg)" backgroundOpacity={0.85}>
                <Stack align="center" justify="center" h="100%" gap="1.5rem" className={sharedStyles.animateScaleIn}>
                    <Warning width={80} height={80} />
                    <Text fw="700" ta="center" size="xl">
                        There was an issue evaluating your results, please select a profile and try again.
                    </Text>
                    <Button onClick={clearEvaluationResults}>Return Home</Button>
                </Stack>
            </Overlay>
        );
    }

    if (resultsOverlay) {
        return (
            <EvaluationResultsOverlay
                title={resultsOverlay.title}
                session={resultsOverlay.session}
                onBack={() => setResultsOverlay(undefined)}
                onViewTranscript={() => {
                    const sessionId = resultsOverlay.session.evaluationDetails[0]?.SessionId;
                    if (sessionId) {
                        void viewResultsBySessionId(sessionId);
                    }
                }}
            />
        );
    }

    return (
        <div className={classes.background}>
            <div className={classes.container}>
                <div className={classes.expertUniversityColumn}>
                    <Stack gap="1.5rem" h="100%">
                        <Group gap="4">
                            <Text
                                fw={700}
                                c="var(--mantine-color-dark-3)"
                                fz="2rem"
                                lh="1.5rem"
                                className={styles.title}
                            >
                                Select a Profile
                            </Text>
                            <LaunchCallSandboxButton />
                        </Group>
                        <Group align="center" gap="md">
                            {shouldShowInProgressProfiles && (
                                <Switch
                                    label="Show in progress profiles"
                                    checked={showInProgressProfiles}
                                    onChange={(event) => setShowInProgressOverride(event.currentTarget.checked)}
                                    fw={700}
                                    pl="0.5rem"
                                    c="var(--mantine-color-dark-3)"
                                />
                            )}
                            <Anchor
                                component="button"
                                type="button"
                                ml="auto"
                                fw={700}
                                c="var(--mantine-color-success-4)"
                                onClick={() => setOpenBlocks(value.length > 0 ? [] : visibleKeys)}
                            >
                                {value.length > 0 ? "Collapse all" : "Expand all"}
                            </Anchor>
                        </Group>
                        <ScrollArea offsetScrollbars scrollbarSize={8} classNames={{ viewport: styles.scrollViewport }}>
                            <Accordion
                                multiple
                                chevronPosition="right"
                                value={value}
                                onChange={setOpenBlocks}
                                classNames={{
                                    item: styles.accordionItem,
                                    chevron: styles.accordionChevron,
                                    content: styles.accordionContent,
                                    control: styles.accordionControl,
                                    label: styles.accordionLabel,
                                }}
                            >
                                {visibleGroups.map((group) => (
                                    <Accordion.Item key={group.key} value={group.key}>
                                        <Accordion.Control>{group.label}</Accordion.Control>
                                        <Accordion.Panel>
                                            {group.profiles.length ? (
                                                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                                                    {group.profiles.map((profile) => {
                                                        const trainingProfileSessions = expertTrainingSessions?.find(
                                                            (session) => session.challengeId === profile.id,
                                                        );

                                                        // Default to the first session; if the expert has taken the
                                                        // challenge more than once, use the most recent by timestamp.
                                                        let latestSession:
                                                            | TrainingSessionEvaluationResults
                                                            | undefined =
                                                            trainingProfileSessions?.evaluationDetails?.[0];
                                                        if (
                                                            trainingProfileSessions?.challengeTaken &&
                                                            trainingProfileSessions.evaluationDetails.length > 1
                                                        ) {
                                                            latestSession =
                                                                trainingProfileSessions.evaluationDetails.reduce(
                                                                    (a, b) => {
                                                                        const previousSessionTime = a.Timestamp ?? 0;
                                                                        const currentSessionTime = b.Timestamp ?? 0;
                                                                        return new Date(previousSessionTime) >
                                                                            new Date(currentSessionTime)
                                                                            ? a
                                                                            : b;
                                                                    },
                                                                );
                                                        }
                                                        const badge =
                                                            profile.simulationId && showInProgressProfiles ? (
                                                                <Badge
                                                                    color="gray.8"
                                                                    styles={{
                                                                        root: {
                                                                            borderRadius: "0",
                                                                            padding: "0.25rem",
                                                                        },
                                                                    }}
                                                                >
                                                                    SIMID: {profile.simulationId}
                                                                </Badge>
                                                            ) : undefined;

                                                        return (
                                                            <TrainingProfileTile
                                                                key={profile.id}
                                                                id={profile.id}
                                                                description={profile.description}
                                                                title={getProfileBlockLabel(profile)}
                                                                challengeTaken={
                                                                    trainingProfileSessions?.challengeTaken ?? false
                                                                }
                                                                challengeStatus={latestSession?.Results}
                                                                badge={badge}
                                                                onStart={async () => {
                                                                    const isInLiveKitPilot = await getFeature<boolean>(
                                                                        "EWP-isInLiveKitPilot",
                                                                        {
                                                                            profile: profile.id,
                                                                            partner: partner as AllowedPartners,
                                                                        },
                                                                    );
                                                                    trackFullStoryEvent(
                                                                        "Expert_University_Training_Session_Started",
                                                                        { profileId: profile.id },
                                                                    );
                                                                    if (expertUniversitySdkEventBus) {
                                                                        logger.info(
                                                                            { profileId: profile.id, isAscend: true },
                                                                            "CALL CREATE TRAINING TASK",
                                                                        );
                                                                        setTaskInSandbox(false);
                                                                        if (isInLiveKitPilot) {
                                                                            beginCallStart({
                                                                                profileId: profile.id,
                                                                                partner: partner as AllowedPartners,
                                                                                isAscendProfile: true,
                                                                                isLiveKitSession: true,
                                                                            });
                                                                        }
                                                                        expertUniversitySdkEventBus.emit(
                                                                            "start-simulation-training",
                                                                            {
                                                                                profileId: profile.id,
                                                                                partner: partner as AllowedPartners,
                                                                                liveKitConfiguration: {
                                                                                    isLiveKitSession: isInLiveKitPilot,
                                                                                    isAscendProfile: true,
                                                                                },
                                                                            },
                                                                        );
                                                                    }
                                                                }}
                                                                onViewResults={async () => {
                                                                    if (trainingProfileSessions) {
                                                                        setResultsOverlay({
                                                                            session: trainingProfileSessions,
                                                                            title: getProfileBlockLabel(profile),
                                                                        });
                                                                    }
                                                                    trackFullStoryEvent(
                                                                        "Expert_University_View_Results_Called",
                                                                        {
                                                                            sessionId: latestSession?.SessionId,
                                                                            profileId: profile.id,
                                                                        },
                                                                    );
                                                                    logger.info(
                                                                        {
                                                                            profileId: profile.id,
                                                                            sessionId: latestSession?.SessionId,
                                                                            isAscend: true,
                                                                        },
                                                                        "CALL VIEW RESULTS",
                                                                    );
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </SimpleGrid>
                                            ) : (
                                                <Text c="dimmed" size="sm">
                                                    No profiles yet
                                                </Text>
                                            )}
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </ScrollArea>
                    </Stack>
                </div>
                <div className={classes.expertUniversityColumn}>
                    <Sidebar partner={partner as AllowedPartners} />
                </div>
            </div>
        </div>
    );
};

export default ExpertUniversityLearningBlocks;
